<template>
  <div>
    <new-langue-popup
      :data-to-show="isNewLanguePopupDisplayed"
      @close="changeStateNewLanguePopup(false)"
      @addLangue="
        (newValue) => {
          addNewLangue(newValue);
        }
      "
      @modifyLangue="
        (newValue) => {
          modifyLangue(newValue);
        }
      "
      :langueToEdit="langues[indexLangueToEdit]"
      :niveaux="niveaux"
    />
    <h3 class="my-15 mx-10">Langues</h3>

    <div
      v-if="isErrorMaxLengthDisplayed"
      class="text-pink-600 text-base text-center mb-8"
    >
      Vous ne pouvez pas sélectionner plus de 3 langues.
    </div>
    <div
      v-if="isErrorMinLengthDisplayed"
      class="text-pink-600 text-base text-center mb-8"
    >
      Afin d'avoir un rendu visuel optimal, il faut sélectionner au moins une
      langue.
    </div>

    <div v-if="langues.length === 0">
      <h6 class="mx-15 text-gray-600">
        Aucune langue vivante n'a été renseignée sur votre Neopass.
      </h6>
      <h6 class="mx-15 text-gray-600 mt-5">
        Pour mettre en avant vos compétences linguistiques, cliquer sur le
        bouton "ajouter une nouvelle langue vivante"
      </h6>

      <div class="flex justify-center mt-15">
        <action-button
          border-color="border-transparent"
          text-color="text-white"
          bg-color="bg-indigo-600"
          bg-color-square="bg-indigo-600"
          icon-color="text-white"
          libelle="Ajouter une nouvelle langue vivante"
          :uppercase="true"
          icon="plus-circle"
          type-shadow="indigo-shadow"
          @clickAction="isNewLanguePopupDisplayed = true"
        />
      </div>

      <div class="flex justify-center mt-10">
        <illustration name="evaluation" />
      </div>
    </div>

    <div v-if="langues.length > 0">
      <draggable
        v-model="form.data.langues"
        v-bind="dragOptions"
        @end="drag = false"
        @start="drag = true"
        handle=".handle"
      >
        <transition-group
          enter-active-class="ease-in duration-1000"
          enter-class="bg-indigo-300 border-0 rounded-xl"
          enter-to-class="bg-none border-0 rounded-xl"
          leave-active-class="linear duration-1000"
          leave-class="h-20 opacity-100"
          leave-to-class="h-0 opacity-0"
          tag="div"
        >
          <div
            v-for="(langue, i) in langues"
            :key="'langue' + i"
            class="h-20 grid grid-cols-10 gap-x-4 pl-5 md:pl-0 py-5 odd:bg-gray-200 odd:bg-opacity-50 items-center"
          >
            <div
              class="col-span-1 md:col-span-2 justify-self-end md:justify-self-center handle"
            >
              <icon
                name="move"
                class="text-indigo-600 w-5 h-5 cursor-pointer"
              />
            </div>
            <div class="col-span-4 text-base">
              {{ langue.value.name }}
            </div>
            <div class="col-span-2 text-base font-light italic">
              {{ niveaux[langue.value.niveau] }}
            </div>
            <div class="col-span-2 flex space-x-3">
              <radio-button
                :name="'langue_' + i + '_display'"
                :value="
                  ((((form || {}).data || {}).langues || [])[i] || {})
                    .isDisplayed || false
                "
                @input="
                  (newValue) => {
                    displayLangue(i);
                  }
                "
                @loseFocus="saveDatas()"
              />
              <div
                class="cursor-pointer px-2 relative"
                @click.stop="displayMenuMore(i)"
              >
                <icon name="more-vertical" class="text-pink-600" />
                <div
                  v-if="menuMoreDisplayed === i"
                  v-click-outside="hideMenuMore"
                  class="absolute text-gray-600 bg-white px-5 py-4 rounded-xl shadow-lg flex flex-col space-y-2 text-sm -right-full z-10"
                >
                  <p
                    @click.stop="displayModifyLanguePopup(i)"
                    class="hover:text-gray-900 cursor-pointer"
                  >
                    Modifier
                  </p>
                  <p
                    @click.stop="removeLangue(i)"
                    class="hover:text-gray-900 cursor-pointer"
                  >
                    Supprimer
                  </p>
                </div>
              </div>
            </div>
          </div>
        </transition-group>
      </draggable>

      <add-button
        class="mb-8 mx-10 mt-8"
        libelle="Ajouter une nouvelle langue vivante"
        @add="isNewLanguePopupDisplayed = true"
      />
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import ClickOutside from 'vue-click-outside';
import draggable from 'vuedraggable';

import NewLanguePopup from './../popup/NewLanguePopup.vue';

import Icon from './../../../components/Icon.vue';
import AddButton from './../../../components/Inputs/AddButton.vue';
import RadioButton from './../../../components/Inputs/RadioButton.vue';
import ActionButton from './../../../components/ActionButton.vue';
import Illustration from './../../../components/Illustration.vue';

import Constants from './../../../constants';

export default {
  components: {
    AddButton,
    Icon,
    RadioButton,
    NewLanguePopup,
    draggable,
    ActionButton,
    Illustration,
  },

  data() {
    return {
      isNewLanguePopupDisplayed: false,
      menuMoreDisplayed: -1,
      indexLangueToEdit: -1,
      isErrorMinLengthDisplayed: false,
      isErrorMaxLengthDisplayed: false,
    };
  },

  methods: {
    ...mapActions({
      saveDatas: 'curriculumVitae/saveDatas',
    }),

    changeStateNewLanguePopup(state) {
      if (!state) {
        this.indexLangueToEdit = -1;
      }
      this.isNewLanguePopupDisplayed = state;
    },

    addNewLangue(value) {
      this.form.data.langues.push({
        fromNeopass: false,
        isDisplayed:
          this.isDashboardCv && this.nbDisplayedLangues >= 1 ? false : true,
        value: value,
      });
    },

    displayMenuMore(num) {
      this.menuMoreDisplayed = num;
    },

    hideMenuMore() {
      this.menuMoreDisplayed = -1;
    },

    displayModifyLanguePopup(num) {
      this.indexLangueToEdit = num;
      this.changeStateNewLanguePopup(true);
      this.hideMenuMore();
    },

    removeLangue(num) {
      this.form.data.langues.splice(num, 1);
      this.hideMenuMore();
    },

    modifyLangue(newValue) {
      this.form.data.langues[this.indexLangueToEdit].value.value = newValue;
      this.indexLangueToEdit = -1;
    },

    displayLangue(index) {
      if (this.isDashboardCv) {
        if (
          !this.form.data.langues[index].isDisplayed &&
          this.nbDisplayedLangues >= 3
        ) {
          this.isErrorMaxLengthDisplayed = true;
          return;
        }
        if (
          this.form.data.langues[index].isDisplayed &&
          this.nbDisplayedLangues === 3
        ) {
          this.isErrorMaxLengthDisplayed = false;
        }
        if (
          this.form.data.langues[index].isDisplayed &&
          this.nbDisplayedLangues <= 1
        ) {
          this.isErrorMinLengthDisplayed = true;
        }
        if (
          !this.form.data.langues[index].isDisplayed &&
          this.nbDisplayedLangues >= 0
        ) {
          this.isErrorMinLengthDisplayed = false;
        }
      }

      this.form.data.langues[index].isDisplayed =
        !this.form.data.langues[index].isDisplayed;
      this.$forceUpdate();
    },
  },

  computed: {
    ...mapGetters({
      form: 'curriculumVitae/form',
    }),

    langues() {
      return this.form.data.langues;
    },

    dragOptions() {
      return {
        animation: 500,
        ghostClass: 'bg-indigo-300',
      };
    },

    isDashboardCv() {
      return [Constants.CV_TYPES.DASHBOARD].includes(this.form.type);
    },

    niveaux() {
      return ['Débutant', 'Intermédiaire', 'Courant', 'Avancé', 'Bilingue'];
    },

    nbDisplayedLangues() {
      this.backdoor; //for reactivity

      return [...new Set(Object.values(this.langues).flat())].filter(
        (h) => h.isDisplayed
      ).length;
    },
  },

  watch: {
    form: {
      handler() {
        this.saveDatas();
      },
      deep: true,
    },
  },

  directives: {
    ClickOutside,
  },
};
</script>
